import {
  Employee,
  EmployeeLeaveSummary,
  LeaveAllowanceChunk,
  LeaveAllowanceChunkMocked,
  LeavePeriod,
  LeavePolicy,
  LeaveRequest,
  LeaveRequestStatusEnum,
  LeaveType,
  LeaveTypeLeaveAllowanceTypeEnum,
} from '@/../api/v1';
import i18n from '@/i18n';
import { MINUTES_IN_HOUR } from '@/lib/date-time/constants';
import { PlainDate } from '@/lib/date-time/PlainDate';
import {
  LeaveEntitlementUnitEnum,
  LeaveEntitlementUnitType,
} from '@/lib/leave/leaveTypeTypes';
import {
  earliestDate,
  latestDate,
  startOfNextDay,
} from '@/util/dateArithmetic';
import {
  endDateToDateTime,
  exclusiveDisplayDate,
  startDateToDateTime,
} from '@/util/dateFunctions';
import { round, roundToStep } from '@/util/mathFunctions';
import { keyOfFirstTruthyValue } from '@/util/objectFunctions';
import { Permission, userHasPermission } from '@/util/permissionFunctions';
import spacetime from 'spacetime';
import { Spacetime } from 'spacetime/types/types.d';

export const leaveStartDate = (
  leave: { startDate: PlainDate; startsMidday: boolean },
  timezone: string,
) => {
  return startDateToDateTime(leave.startDate, leave.startsMidday, timezone);
};
export const leaveEndDate = (
  leave: { endDate: PlainDate; endsMidday: boolean },
  timezone: string,
) => {
  return endDateToDateTime(leave.endDate, leave.endsMidday, timezone);
};

export const isApproved = ({ status }: LeaveRequest) =>
  status === LeaveRequestStatusEnum.Approved;

export const isPendingApproval = ({ status }: LeaveRequest) =>
  status === LeaveRequestStatusEnum.PendingApproval;

export const isPendingCancellation = ({ status }: LeaveRequest) =>
  status === LeaveRequestStatusEnum.PendingCancellation;

export const isPending = (request: LeaveRequest) =>
  isPendingApproval(request) || isPendingCancellation(request);

export const isApprovable = (request: LeaveRequest) =>
  isPendingApproval(request) || isPendingCancellation(request);

export const formatLeaveTime = (
  leave: LeaveRequest,
  timezone: string,
  showDate: boolean,
): string => {
  const { endsMidday } = leave;

  const startDate = leaveStartDate(leave, timezone);
  const endDate = leaveEndDate(leave, timezone);

  const spaceStart = spacetime(startDate, timezone);
  let spaceEnd = spacetime(endDate, timezone);

  if (spaceStart.diff(spaceEnd, 'day') < 1) {
    return (
      endsMidday ? i18n.t('time.morning') : i18n.t('time.afternoon')
    ).toString();
  }
  if (spaceStart.diff(spaceEnd, 'day') === 1 || !showDate)
    return i18n.t('label.fullDay').toString();

  if (!endsMidday) spaceEnd = spaceEnd.subtract(1, 'day');
  const timeString = i18n.d(startDate, 'dayMonth');
  if (spaceStart.isSame(spaceEnd, 'day')) return timeString;

  return `${timeString} - ${i18n.d(spaceEnd.toNativeDate(), 'dayMonth')}`;
};

export const presentLeaveDates = (
  leaveRequest: LeaveRequest,
  timezone: string,
  { day, month, year } = {
    day: '{date-ordinal}',
    month: '{month-short}',
    year: '{year}',
  },
): string => {
  const spaceStart = spacetime(
    leaveStartDate(leaveRequest, timezone),
    timezone,
  );
  const spaceEnd = spacetime(
    exclusiveDisplayDate(leaveEndDate(leaveRequest, timezone), timezone),
    timezone,
  );

  const startDateHalfDayText = leaveRequest.startsMidday
    ? i18n.tc('input.pm').toLowerCase()
    : '';
  const endDateHalfDayText = leaveRequest.endsMidday
    ? i18n.tc('input.am').toLowerCase()
    : '';

  const formatDate = (
    spaceDate: Spacetime,
    format: string,
    halfDayText: string,
  ): string =>
    `${spaceDate.format(format)} ${
      halfDayText ? `(${halfDayText})` : ''
    }`.trim();

  if (spaceStart.isSame(spaceEnd, 'day')) {
    return formatDate(
      spaceStart,
      `${day} ${month} ${year}`,
      startDateHalfDayText || endDateHalfDayText,
    );
  }

  const startFormat = keyOfFirstTruthyValue({
    [`${day}`]: spaceStart.isSame(spaceEnd, 'month'),
    [`${day} ${month}`]: spaceStart.isSame(spaceEnd, 'year'),
    [`${day} ${month} ${year}`]: true,
  }) as string;

  return `${formatDate(
    spaceStart,
    startFormat,
    startDateHalfDayText,
  )} - ${formatDate(spaceEnd, `${day} ${month} ${year}`, endDateHalfDayText)}`;
};

export const halfDayText = (request: LeaveRequest): string => {
  // If we ever change our leave requests to allow multi-day leave that can
  // start or end at midday, then this would need making more sophisticated.
  if (request.endsMidday) {
    return i18n.t('time.morning').toString();
  }
  if (request.startsMidday) {
    return i18n.t('time.afternoon').toString();
  }
  return '';
};

export const leaveDayText = (
  leaveRequest: LeaveRequest,
  start: Date,
  end: Date,
  timezone: string,
): string => {
  const presentationalEndDate = spacetime(end, timezone)
    .subtract(1, 'day')
    .toNativeDate();

  const leaveStart = leaveStartDate(leaveRequest, timezone);
  const leaveEnd = leaveEndDate(leaveRequest, timezone);
  const isStartDay = spacetime(start, timezone).isSame(
    spacetime(leaveStart, timezone),
    'day',
  );
  const isEndDay = spacetime(presentationalEndDate, timezone).isSame(
    spacetime(leaveEnd, timezone),
    'day',
  );

  return (keyOfFirstTruthyValue({
    [i18n.tc('time.afternoon')]: isStartDay && leaveRequest.startsMidday,
    [i18n.tc('time.morning')]:
      (isStartDay || isEndDay) && leaveRequest.endsMidday,
  }) || i18n.tc('label.fullDay')) as string;
};

export const presentLeaveDuration = (
  amount: number,
  leaveEntitlementUnit: LeaveEntitlementUnitType,
): string => {
  return LeaveEntitlementUnitEnum.Hours === leaveEntitlementUnit
    ? i18n.tc('unitWithAmount.hour', amount === 1 || amount === -1 ? 1 : 2, {
        n: round(amount, 2),
      })
    : i18n.tc('unitWithAmount.day', amount === 1 || amount === -1 ? 1 : 2, {
        n: round(amount, 2),
      });
};

export const presentLeaveUnit = (
  amount: number,
  leaveEntitlementUnit: LeaveEntitlementUnitType,
): string => {
  return leaveEntitlementUnit === LeaveEntitlementUnitEnum.Hours
    ? i18n.tc('unit.hour', amount === 1 ? 1 : 2)
    : i18n.tc('unit.day', amount === 1 ? 1 : 2);
};

export const totalLeaveAllowanceUsed = (
  leaveAllowanceChunks: Array<LeaveAllowanceChunk | LeaveAllowanceChunkMocked>,
  leaveAllowanceUnit: LeaveEntitlementUnitType,
) => {
  if (!leaveAllowanceChunks.length) {
    return 0;
  }
  const allowanceProperty =
    leaveAllowanceUnit === LeaveEntitlementUnitEnum.Hours
      ? 'allowanceUsedInHours'
      : 'allowanceUsedInDays';
  return round(
    leaveAllowanceChunks.reduce(
      (acc, curr) => acc + curr[allowanceProperty],
      0,
    ),
    2,
  );
};

export const leaveAllowanceUsedForPeriod = (
  leaveRequest: LeaveRequest,
  leavePeriod: LeavePeriod | undefined,
) => {
  if (!leaveRequest.leaveAllowanceChunks.length || !leavePeriod) {
    return 0;
  }
  const chunks = leaveRequest.leaveAllowanceChunks.filter(
    (c) => c.date >= leavePeriod.startDate && c.date <= leavePeriod.endDate,
  );

  return totalLeaveAllowanceUsed(
    chunks,
    leaveRequest.baseLeaveAllowanceUnit as any,
  );
};

export const leaveIsPaid = (leaveRequest: LeaveRequest): boolean =>
  leaveRequest.paid ||
  leaveRequest.leaveAllowanceChunks.some((c) => c.paidHours > 0);

export const getLeaveRequestRangePaidHours = (
  leave: LeaveRequest,
  start: PlainDate,
  end: PlainDate,
) => {
  if (!leaveIsPaid(leave)) {
    return 0;
  }

  return leave.leaveAllowanceChunks
    .filter(
      (chunk) =>
        !!chunk.paidHours &&
        PlainDate.compare(chunk.date, start) >= 0 &&
        PlainDate.compare(chunk.date, end) <= 0,
    )
    .reduce((acc, { paidHours }) => {
      return acc + paidHours;
    }, 0);
};

export const getLeaveRequestRangeAllowanceHours = (
  leave: LeaveRequest,
  start: PlainDate,
  end: PlainDate,
) => {
  return leave.leaveAllowanceChunks
    .filter(
      (chunk) =>
        PlainDate.compare(chunk.date, start) >= 0 &&
        PlainDate.compare(chunk.date, end) <= 0,
    )
    .reduce((acc, { allowanceUsedInHours }) => {
      return acc + allowanceUsedInHours;
    }, 0);
};

export const getLeaveRequestDayPaidMinutes = (
  leave: LeaveRequest,
  date: Date,
  timezone: string,
): number => {
  const plainDate = PlainDate.fromDate(date, timezone);
  return (
    getLeaveRequestRangePaidHours(leave, plainDate, plainDate) * MINUTES_IN_HOUR
  );
};

/**
 * This function will return the total days covered by a leave request
 * If a start or end date are provided then it will limit the leave request dates
 * */
export const getLeaveDaysCoveredInPeriod = (
  leave: {
    startDate: PlainDate;
    startsMidday: boolean;
    endDate: PlainDate;
    endsMidday: boolean;
  },
  timezone: string,
  startDate: Date | null = null,
  endDate: Date | null = null,
): number => {
  const leaveStart = startDate
    ? latestDate<Date>([startDate, leaveStartDate(leave, timezone)])
    : leaveStartDate(leave, timezone);
  const leaveEnd = endDate
    ? earliestDate([
        startOfNextDay(endDate, timezone),
        leaveEndDate(leave, timezone),
      ])
    : leaveEndDate(leave, timezone);
  return roundToStep(
    spacetime(leaveStart, timezone).diff(
      spacetime(leaveEnd, timezone),
      'hour',
    ) / 24,
    0.5,
  );
};

export const getLeaveAllowanceUsedPercentage = (
  employeeLeaveSummary: EmployeeLeaveSummary,
): number => {
  // The EmployeeLeaveSummary does not take adjustments into account for 'Booked' leave
  const bookedLeave =
    employeeLeaveSummary.booked + employeeLeaveSummary.adjustment;
  return employeeLeaveSummary.allowance === null
    ? 0
    : Math.abs((bookedLeave / employeeLeaveSummary.allowance) * 100);
};

export const leaveAllowanceRemainingColour = (
  percentageUsed: number,
  policy: LeavePolicy,
  leaveType: LeaveType | undefined,
): string => {
  const isAccruedType =
    leaveType?.leaveAllowanceType === LeaveTypeLeaveAllowanceTypeEnum.Accrued;
  const hideColours =
    policy?.leaveAllowanceCanBeExceeded || percentageUsed === null;

  return keyOfFirstTruthyValue({
    'text-gray-900': hideColours,
    'text-green-800': isAccruedType ? percentageUsed < 90 : percentageUsed < 40,
    'text-yellow-900': !isAccruedType && percentageUsed < 90,
    'text-red': true,
  });
};

export const fragmentLeaveRequest = (
  leaveRequest: LeaveRequest,
): LeaveRequest[] => {
  return leaveRequest.leaveAllowanceChunks.map((chunk) => ({
    ...leaveRequest,
    leaveAllowanceChunks: [chunk],
    startDate: chunk.date,
    endDate: chunk.date,
    startsMidday:
      chunk.date.toString() === leaveRequest.startDate.toString()
        ? leaveRequest.startsMidday
        : false,
    endsMidday:
      chunk.date.toString() === leaveRequest.endDate.toString()
        ? leaveRequest.endsMidday
        : false,
  }));
};

export const getMinLeaveStartDate = (
  targetEmployee: Employee,
  requestingEmployee: Employee,
  leavePolicy: LeavePolicy,
  timezone: string,
): Date | null => {
  // Line manager, leave approver or manage leave allows the user to add leave without a notice period
  if (
    userHasPermission(Permission.ManageLeave) ||
    targetEmployee.lineManagerId === requestingEmployee.id ||
    targetEmployee.leaveApproverId === requestingEmployee.id ||
    !leavePolicy
  ) {
    return null;
  }

  return spacetime
    .now(timezone)
    .add(leavePolicy.leaveNoticePeriodInDays, 'day')
    .startOf('day')
    .toNativeDate();
};
